<template>
  <div class="model-navigation-container">
    <!-- Prev button -->
    <button
      :aria-disabled="prevDisabled"
      :aria-label="$t('general.previous_set')"
      :class="{ 'button--disabled': prevDisabled }"
      type="button"
      @click="handleScrollerNavigation('previous')"
    >
      <ArrowNavIconUp />
    </button>
    <!-- Nav items -->
    <nav
      v-if="fullSetList"
      class="model-navigation scroll-container scroll-container--unpadded"
    >
      <a
        v-for="navItem in fullSetList"
        :key="navItem"
        :aria-disabled="isDisabled(navItem)"
        :class="[
          'model-navigation__link',
          navItemClasses(isDisabled(navItem))
        ]"
        @click.prevent="handleSetItemNavigation(navItem)"
      >
        <div
          class="model-navigation__link-inner"
          :class="[
            navItemClasses(isDisabled(navItem)),
            setActiveClass(navItem)
          ]"
        >
          {{ navItem }}
        </div>
        <!-- Station indicators -->
        <template
          v-for="stationItem in stations"
          :key="stationItem"
        >
          <StationItem
            v-if="isStationStart(navItem, stationItem) && notSingleStation(stationItem)"
            :modifiers="['start']"
            :stationData="stationItem"
          />
          <StationItem
            v-if="isRegularStation(navItem, stationItem)"
            :stationData="stationItem"
          />
          <StationItem
            v-if="isStationEnd(navItem, stationItem) && notSingleStation(stationItem)"
            :modifiers="['end']"
            :stationData="stationItem"
          />
          <StationItem
            v-if="isStationStart(navItem, stationItem) && isStationEnd(navItem, stationItem)"
            :modifiers="['single']"
            :stationData="stationItem"
          />
        </template>
      </a>
    </nav>
    <!-- Next button -->
    <button
      :aria-label="$t('general.next_set')"
      :class="{ 'button--disabled': nextDisabled }"
      type="button"
      @click="handleScrollerNavigation('next')"
    >
      <ArrowNavIconDown />
    </button>
  </div>
</template>

<script setup>
import { ref, computed, watchEffect, watch, nextTick, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';

// Components
import StationItem from '@/components/shaftsV2/shaft3dModel/shaft_3d_model_set_navigation_station_item.vue';

// Icons
import ArrowNavIconUp from '@/images/arrow-nav-icon-up.svg';
import ArrowNavIconDown from '@/images/arrow-nav-icon-down.svg';

const props = defineProps({
  /** The current set that has been selected */
  currentSet: { type: Number, required: false, default: null },
  /** The number of sets a shaft is configured with. .eg 200 */
  totalNumbeOfSets: { type: Number, required: false, default: 0 },
  /** An array that lists the sets that have issues */
  setsWithIssues: { type: Array, required: false, default: () => [] },
  /** An array that lists the stations and their details */
  stations: { type: Array, required: false, default: () => [] },
  /** Toggles between the overlay and normal location page */
  isOverlay: { type: Boolean, required: false, default: false },
});

const emit = defineEmits(['station-name']);

const currentNavigationIndex = ref(0);
const nextDisabled = ref(false);
const prevDisabled = ref(false);
const route = useRoute();
const router = useRouter();
const updateSet = ref(false);

const fullSetList = computed(() => {
  let list = [];
  for (let set = 1; set <= props.totalNumbeOfSets; set++) {
    list.push(set);
  }
  return list;
});

const setsWithIssuesList = computed(() =>
  props.setsWithIssues.map((set) => set.value)
);

const hasSetsWithIssues = computed(() => props.setsWithIssues.length !== 0);

onMounted(() => {
  scrollToCurrentSet();
});

watch(() => props.currentSet, async () => {
  scrollToCurrentSet();
});

/**
 * Triggers scrolling to the active set
 */
async function scrollToCurrentSet() {
  await nextTick();

  let activeSet = document.querySelector('.model-navigation__link--active');
  if (activeSet) {
    getStationNameOnSet(activeSet);

    activeSet.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center'
    });
  }
}

/**
 * Checks if a station element exists in the navigation
 * and emits the data back to it's parent component
 * @param {object} currentSet
 */
function getStationNameOnSet(currentSet) {
  if (currentSet.nextElementSibling && currentSet.nextElementSibling.dataset.station) {
    emit('station-name', currentSet.nextElementSibling.dataset.station);
  }
}

/**
 * Tracks the index position of the currestSet (active link)
 * in the relevant data source
 */
watchEffect(() => {
  if (props.currentSet && hasSetsWithIssues.value) {
    currentNavigationIndex.value = setsWithIssuesList.value.indexOf(props.currentSet);
    navigationDisabledState(setsWithIssuesList.value);
  } else {
    currentNavigationIndex.value = fullSetList.value.indexOf(props.currentSet);
    navigationDisabledState(fullSetList.value);
  }
});

/**
 * Routes for issues
 * @param {number} setNumber
 */
function routeToIssues(setNumber) {
  router.push({
    name: 'shaftV2IssuesTypeSet',
    params: { setNumber: setNumber },
  });
}

/**
 * Routes for issue location
 * @param {number} setNumber
 */
function routeToIssueLocation(setNumber) {
  const overlayPageName = route.params?.setGroupId ? 'shaftV2IssuesLocationTypeOverviewId' : 'shaftV2IssuesLocationTypeOverview';
  const isOverview = route.fullPath.includes('overview');
  const pageName = isOverview ? overlayPageName : 'shaftV2IssuesLocationTypeSet';
  router.push({
    name: props.isOverlay ? pageName : 'shaftV2IssueLocationSet',
    params: { setId: setNumber, setNumber, issueId: route.params.issueId },
  });
}

/**
 * Handles navigation when clicking on a set number
 * @param {number} setNumber
 */
function handleSetItemNavigation(setNumber) {
  updateSet.value = true;
  if (hasSetsWithIssues.value) {
    routeToIssues(setNumber);
  } else {
    routeToIssueLocation(setNumber);
  }
}

/**
 * Handles navigation when clicking the next/previous buttons
 * @param {string} direction
 */
function handleScrollerNavigation(direction) {
  let newIndex =
    direction === 'next'
      ? currentNavigationIndex.value + 1
      : currentNavigationIndex.value - 1;

  if (hasSetsWithIssues.value) {
    routeToIssues(setsWithIssuesList.value[newIndex]);
  } else {
    routeToIssueLocation(fullSetList.value[newIndex]);
  }
}

/**
 * Enables or disables the next/prev button depending on
 * position of the currentSet (active link)
 * @param {array} arr
 */
function navigationDisabledState(arr) {
  nextDisabled.value = arr.at(-1) === props.currentSet;
  prevDisabled.value = arr.at(0) === props.currentSet;

  if (!props.currentSet || isNaN(props.currentSet)) {
    prevDisabled.value = true;
  }
}

function setActiveClass(setItem) {
  return props.currentSet === setItem
    ? 'model-navigation__link--active typo-small-title'
    : null;
}

function isDisabled(setItem) {
  if (props.setsWithIssues.length === 0) return false;
  return !props.setsWithIssues.map((set) => set.value).includes(setItem);
}

function navItemClasses(disabled) {
  return {
    'typo-super-tiny': true,
    'model-navigation__link': true,
    'model-navigation__link--disabled': disabled,
  };
}

function notSingleStation(stationItem) {
  return stationItem.start_set !== stationItem.end_set;
}

function isStationStart(navItem, stationItem) {
  return navItem === stationItem.start_set;
}

function isStationEnd(navItem, stationItem) {
  return navItem === stationItem.end_set;
}

function isRegularStation(navItem, stationItem) {
  return (navItem > stationItem.start_set) && (navItem < stationItem.end_set);
}

defineExpose({updateSet});
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

$linkSize: rem(30);

.model-navigation-container {
  bottom: 0;
  display: grid;
  grid-template-rows: $linkSize 1fr $linkSize;
  left: 0;
  padding: bu(2) 0;
  position: absolute;
  right: 0;
  top: 0;

  button {
    align-items: center;
    display: flex;
    justify-content: center;
    user-select: none;
  }
}

.model-navigation {
  @include hide-scrollbars;
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: bu(1) 0;
  overflow-y: scroll;
  position: relative;
  padding-bottom: bu(2);

  @include breakpoint(medium) {
    height: initial;
    position: initial;
    padding-bottom: 0;
  }

  &__link {
    align-items: center;
    border-radius: 100%;
    color: map-get($foreground-color, dark);
    display: flex;
    justify-content: center;
    min-height: $linkSize;
    pointer-events: initial;
    position: relative;
    user-select: none;
    width: $linkSize;

    &:hover {
      color: $white;
      cursor: pointer;
    }

    &--disabled {
      color: transparentize(map-get($foreground-color, dark), 0.8);
      pointer-events: none;
    }
  }

  &__link-inner {
    bottom: 0;
    position: absolute;
    top: 0;
    z-index: 1;
  }

  &__link--active {
    background-color: $white;
    color: map-get($background-color, default);
    font-weight: 700;

    &:hover {
      color: map-get($background-color, default);
    }
  }
}
</style>
